import { html, LitElement, unsafeCSS } from 'lit';
import { customElement } from 'lit/decorators/custom-element.js';
import { property } from 'lit/decorators/property.js';
import { state } from 'lit/decorators/state.js';
import spotlightCSS from './bg-spotlight.scss?inline';

@customElement('bg-spotlight')
export class BgSpotlight extends LitElement {
    static styles = unsafeCSS(spotlightCSS);

    @property({ type: String, attribute: 'items-container' })
    itemsContainer: string = '';

    @property({ type: String, attribute: 'aria-label' })
    ariaLabel: string = 'Interactive content spotlight';

    @state()
    private currentItemIndex = 0;

    @state()
    private spotlightItems: Element[] = [];

    private targetContainer: Element | null = null;
    private observer: IntersectionObserver | null = null;
    private currentClonedItem: Element | null = null;
    private anchorElements: Element[] = [];
    private liveRegion: HTMLElement | null = null;

    connectedCallback() {
        super.connectedCallback();
        // Delay initialization to ensure DOM is ready
        setTimeout(() => {
            this.initializeSpotlight();
            this.setupScrollListener();
            this.createLiveRegion();
        }, 100);
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        this.cleanup();
    }

    private createLiveRegion() {
        // Create a live region for announcing content changes
        this.liveRegion = document.createElement('div');
        this.liveRegion.setAttribute('aria-live', 'polite');
        this.liveRegion.setAttribute('aria-atomic', 'true');
        this.liveRegion.className = 'sr-only';
        this.liveRegion.style.position = 'absolute';
        this.liveRegion.style.width = '1px';
        this.liveRegion.style.height = '1px';
        this.liveRegion.style.padding = '0';
        this.liveRegion.style.margin = '-1px';
        this.liveRegion.style.overflow = 'hidden';
        this.liveRegion.style.clip = 'rect(0, 0, 0, 0)';
        this.liveRegion.style.whiteSpace = 'nowrap';
        this.liveRegion.style.border = '0';
        
        document.body.appendChild(this.liveRegion);
    }

    private announceContentChange(itemIndex: number) {
        if (!this.liveRegion) return;
        
        const totalItems = this.spotlightItems.length;
        const announcement = `Spotlight content updated. Showing item ${itemIndex + 1} of ${totalItems}.`;
        this.liveRegion.textContent = announcement;
    }

    private initializeSpotlight() {
        // Find the target container by ID if specified, otherwise use default behavior
        if (this.itemsContainer) {
            this.targetContainer = document.getElementById(this.itemsContainer);
            if (!this.targetContainer) {
                console.warn(`bg-spotlight: Container with ID "${this.itemsContainer}" not found`);
                return;
            }
        } 

        if (!this.targetContainer) {
            console.warn('bg-spotlight: No target container found');
            return;
        }

        // Find all spotlight items in the target container
        this.spotlightItems = Array.from(this.targetContainer.querySelectorAll('.bg-spotlight-item'));
        
        if (this.spotlightItems.length > 0) {
            this.createAnchorElements();
            this.updateCurrentItem();
        }
    }

    private createAnchorElements() {
        // Create zero-height anchor elements before each spotlight item
        this.anchorElements = [];
        this.spotlightItems.forEach((item, index) => {
            const anchor = document.createElement('div');
            anchor.className = 'bg-spotlight-anchor';
            anchor.style.height = '0px';
            anchor.style.overflow = 'hidden';
            anchor.style.visibility = 'visible';
            anchor.dataset.spotlightIndex = index.toString();
            
            // Insert the anchor before the spotlight item
            item.parentNode?.insertBefore(anchor, item);
            this.anchorElements.push(anchor);
        });
    }

    private setupScrollListener() {
        if (this.anchorElements.length === 0) return;
        
        // Create intersection observer for anchor elements
        this.observer = new IntersectionObserver(
            () => {
                let activeIndex = -1;
                let bestDistance = Infinity;

                // Check all anchors to find which one is closest to viewport center
                this.anchorElements.forEach((anchor, index) => {
                    const rect = anchor.getBoundingClientRect();
                    const viewportMiddle = window.innerHeight / 2;
                    const anchorTop = rect.top;
                    
                    // Only consider anchors that are above the middle line (have passed it)
                    if (anchorTop <= viewportMiddle) {
                        const distance = Math.abs(anchorTop - viewportMiddle);
                        
                        if (distance < bestDistance) {
                            bestDistance = distance;
                            activeIndex = index;
                        }
                    }
                });

                // Update current item if we found a different active one
                if (activeIndex !== -1 && activeIndex !== this.currentItemIndex) {
                    this.currentItemIndex = activeIndex;
                    this.updateCurrentItem();
                    this.announceContentChange(activeIndex);
                }
            },
            {
                threshold: 0,
                // Trigger when elements cross the center line
                rootMargin: '0px 0px -50% 0px'
            }
        );

        this.anchorElements.forEach((anchor) => {
            this.observer!.observe(anchor);
        });
    }

    private updateCurrentItem() {
        // Remove any existing cloned item
        if (this.currentClonedItem) {
            this.currentClonedItem.remove();
            this.currentClonedItem = null;
        }

        if (this.spotlightItems.length === 0) {
            return;
        }

        const currentItem = this.spotlightItems[this.currentItemIndex];
        if (!currentItem) {
            return;
        }

        // Clone the element and its content
        const clonedItem = currentItem.cloneNode(true) as HTMLElement;
        
        // Add fade-in animation class
        clonedItem.classList.add('bg-spotlight-fade-in');
        
        // Add the cloned item to the light DOM (this element's children)
        this.appendChild(clonedItem);
        this.currentClonedItem = clonedItem;
    }

    private cleanup() {
        if (this.observer) {
            this.observer.disconnect();
            this.observer = null;
        }
        
        // Remove any cloned item
        if (this.currentClonedItem) {
            this.currentClonedItem.remove();
            this.currentClonedItem = null;
        }
        
        // Remove anchor elements
        this.anchorElements.forEach((anchor) => {
            anchor.remove();
        });
        this.anchorElements = [];
        
        // Remove live region
        if (this.liveRegion && this.liveRegion.parentNode) {
            this.liveRegion.parentNode.removeChild(this.liveRegion);
            this.liveRegion = null;
        }
    }

    render() {
        return html`
            <div 
                class="bg-spotlight-container" 
                role="region" 
                aria-label="${this.ariaLabel}"
                aria-live="polite"
            >
                <div class="bg-spotlight-content" aria-atomic="true">
                    <slot></slot>
                </div>
                <div class="bg-spotlight-status" aria-live="polite" aria-atomic="true">
                    <span class="sr-only">
                        Item ${this.currentItemIndex + 1} of ${this.spotlightItems.length}
                    </span>
                </div>
            </div>
        `;
    }
} 